import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { FC, useEffect, useState } from "react";
import Button from "~/components/Button";
import Container from "~/components/Container";
import Text from "~/components/Text";
import styles from "~/styles/pages/Auth.module.scss";

import AuthMessage from "~/components/AuthMessage";
import Icon from "~/components/Icon";
import SignUpForm from "~/components/SignUpForm";

import Colors from "~/constants/colors";
import { getTheme, isUsingLayoutV2 } from "~/utils/theme";
import { Identifiers } from "~/constants/enum";
import useCmsBlocks from "~/hooks/useCmsBlocks";
import Skeleton from "react-loading-skeleton";
import { isEmpty } from "lodash";
import classNames from "classnames";

type SignUpStep = "form" | "verifying" | "verified" | "expired";

const SignUp: FC = () => {
  const search = useSearchParams();
  const [step, setStep] = useState<SignUpStep>("form");
  const [email, setEmail] = useState("");
  const { cmsBlocks, loading: loadingCmsBlock } = useCmsBlocks([
    Identifiers.SignUpBanner,
  ]);

  useEffect(() => {
    setStep(search.get("step") as SignUpStep);
  }, [search]);

  const renderContent = () => {
    switch (step) {
      case "verifying":
        return (
          <AuthMessage
            title="Verify your email"
            icon={{ name: "sms", color: Colors.PRIMARY }}
            className="gap-5"
          >
            <Text type="body-2" className="text-center">
              We have sent you an email to{" "}
              <Text type="label-3" htmlTag="span" className="text-[#0F0F0F]">
                {email || ""}
              </Text>{" "}
              that contains a link to verify your account.
            </Text>

            <Text type="body-4" className="my-3 text-center text-grey-500">
              Don&apos;t receive the email? Please check your spam or{" "}
              <Link href="#" className="font-medium text-primary">
                Resend link
              </Link>
            </Text>
            <Link className="self-center" href="/sign-in">
              <Button type="borderless">
                <Icon name="arrow-left" /> Back to sign in
              </Button>
            </Link>
          </AuthMessage>
        );

      case "verified":
        return (
          <AuthMessage
            title="Your email has been verified"
            icon={{ name: "tick-circle", color: "#3BA55C" }}
          >
            <Text type="body-2" className="text-center text-grey-500">
              Now you can sign in to Dunkare
            </Text>
            <Link className="self-center" href="/sign-in">
              <Button type="borderless">
                <Icon name="arrow-left" /> Click here to sign in
              </Button>
            </Link>
          </AuthMessage>
        );

      case "expired":
        return (
          <AuthMessage
            title="The email has expired"
            icon={{ name: "close-circle", color: "#ED4245" }}
          >
            <Text type="body-2" className="text-center text-grey-500">
              The email you clicked has expired.{" "}
              <Link href="#" className="font-medium text-primary-dark">
                Try again
              </Link>
            </Text>
          </AuthMessage>
        );

      default:
        return (
          <SignUpForm
            onSubmit={(e) => {
              setEmail(e);
              setStep("verifying");
            }}
          />
        );
    }
  };

  const banner = cmsBlocks?.[Identifiers.SignUpBanner]?.content;

  return (
    <Container rootClassName={styles.auth_page}>
      <div
        className={classNames(styles.sign_up_wrapper, {
          "!max-w-[1210px] !rounded-none": isUsingLayoutV2,
        })}
      >
        <div>
          {loadingCmsBlock ? (
            <div className={styles.img}>
              <Skeleton height={"100%"} width={"100%"} />
            </div>
          ) : !isEmpty(banner) ? (
            <div
              className={styles.img}
              dangerouslySetInnerHTML={{
                __html: banner,
              }}
            ></div>
          ) : (
            !isUsingLayoutV2 && (
              <div className={styles.img}>
                <Image
                  className="object-cover"
                  src={`/images/sign_up.jpg`}
                  alt="Decorative image"
                  fill
                  priority={false}
                />
              </div>
            )
          )}
        </div>

        <div
          className={classNames({
            "mt-0 lg:mt-10": isUsingLayoutV2,
          })}
        >
          {renderContent()}
        </div>
      </div>
    </Container>
  );
};

export default SignUp;
